import React, { useState } from "react";
import "./CompoundingCalculator.css";

const CompoundingCalculator: React.FC = () => {
    const [amount, setAmount] = useState<number>(1000000); // ₹10,00,000
    const [monthlyPercent, setMonthlyPercent] = useState<number>(5); // 6%
    const [years, setYears] = useState<number>(4); // 4 years

    // Validation ranges
    const minAmount = 0;
    const maxAmount = 10000000000; // 100 Cr
    const minPercent = 0;
    const maxPercent = 100;
    const minYears = 0;
    const maxYears = 100;

    function formatIndianNumber(value: number): string {
        if (value >= 10000000) {
            return (value / 10000000).toFixed(2) + " Cr"; // Crore
        } else {
            return finalValue.toLocaleString("en-IN", { maximumFractionDigits: 2 })        // Normal number
        }
    }

    // Enforce limits inside handlers
    const handleAmount = (val: number) => {
        setAmount(Math.max(minAmount, Math.min(maxAmount, val)));
    };

    const handlePercent = (val: number) => {
        setMonthlyPercent(Math.max(minPercent, Math.min(maxPercent, val)));
    };

    const handleYears = (val: number) => {
        setYears(Math.max(minYears, Math.min(maxYears, val)));
    };

    // ✅ Formula: A = P * (1 + r/100)^(12 * years)
    const finalValue =
        amount * Math.pow(1 + monthlyPercent / 100, 12 * years);

    // Calculate total return percentage
    const totalReturnPercent = amount > 0 ? ((finalValue - amount) / amount) * 100 : 0;

    return (
        <div className="main-content calculator-container">
            <h1 className="title">💹 Swing Trading Profit Calculator</h1>
            <div className="card">
                {/* Amount */}
                <div className="input-group">
                    <label>💰 Initial Capital (₹)</label>
                    <input
                        type="number"
                        value={amount}
                        onChange={(e) => handleAmount(Number(e.target.value))}
                    />
                    <br />
                    <small>
                        {minAmount.toLocaleString("en-IN")} –{" "}
                        {maxAmount.toLocaleString("en-IN")}
                    </small>
                </div>

                {/* Monthly % */}
                <div className="input-group">
                    <label>📈 Profit % per Month</label>
                    <input
                        type="number"
                        step="0.50"
                        value={monthlyPercent}
                        onChange={(e) => handlePercent(Number(e.target.value))}
                    />
                    <br />
                    <small>
                        {minPercent}% – {maxPercent}%
                    </small>
                </div>

                {/* Years */}
                <div className="input-group">
                    <label>⏳ Years</label>
                    <input
                        type="number"
                        value={years}
                        onChange={(e) => handleYears(Number(e.target.value))}
                    />
                    <br />
                    <small>
                        {minYears} – {maxYears}
                    </small>
                </div>

                {/* Result */}
                <div className="result">
                    <h2>Your Capital After {years} Years</h2>
                    {/* <p className="highlight">
            ₹ {finalValue.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
          </p> */}
                    <h2 className="highlight">{formatIndianNumber(finalValue)}</h2>
                    <p className="total-return">
                        Total Return: <span className="return-value">{totalReturnPercent.toFixed(2)}%</span>
                    </p>
                </div>
            </div>

            <div className="notes-wrap">
                <h3 className="notes-title">Notes:</h3>

                <ul className="notes-list">
                    <li>
                        <strong>Capital</strong>: Your starting investment amount.
                    </li>
                    <li>
                        <strong>Monthly Profit (%)</strong>: The average percentage return you
                        expect each month from swing trading.
                    </li>
                    <li>
                        <strong>Years</strong>: The total duration you want to calculate for.
                    </li>
                    <li>
                        This calculator uses <strong>monthly compounding</strong>. Every
                        month, profits are added back into the capital, and the next month’s
                        return is calculated on the increased amount.
                    </li>
                    <li>
                        In swing trading, profits are typically <strong>reinvested</strong>{" "}
                        (compounded), which can significantly increase growth over time.
                    </li>
                </ul>
            </div>
        </div>
    );
};

export default CompoundingCalculator;
